import { restoreRange } from './apply-mutation';
import { closestFromNode, elementFromSelectionNode, getNearestAnchor } from './selection';
import { wrapRangeWithElement } from './wrap-range';

const INLINE_TOGGLE_TAGS: Record<'bold' | 'italic' | 'underline', string[]> = {
    bold: ['strong', 'b'],
    italic: ['em', 'i'],
    underline: ['u']
};

const BLOCK_SELECTOR = 'p, h1, h2, h3, h4, h5, h6, div, li, td, th, blockquote, pre';

const unwrapElement = (element: Element): void => {
    const parent = element.parentNode;
    if (!parent) {
        element.remove();
        return;
    }

    while (element.firstChild) {
        parent.insertBefore(element.firstChild, element);
    }
    parent.removeChild(element);
};

const placeCaretAfter = (document: Document, node: Node): void => {
    const selection = document.getSelection();
    if (!selection) return;

    const range = document.createRange();
    range.setStartAfter(node);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
};

export const insertTextAtRange = (document: Document, range: Range | null, text: string): void => {
    const selection = document.getSelection();
    const activeRange = range ?? (selection?.rangeCount ? selection.getRangeAt(0) : null);
    if (!activeRange) {
        document.body?.appendChild(document.createTextNode(text));
        return;
    }

    activeRange.deleteContents();
    const textNode = document.createTextNode(text);
    activeRange.insertNode(textNode);
    placeCaretAfter(document, textNode);
};

export const insertNodeAtRange = (document: Document, range: Range | null, node: Node): void => {
    const selection = document.getSelection();
    const activeRange = range ?? (selection?.rangeCount ? selection.getRangeAt(0) : null);
    if (!activeRange) {
        document.body?.appendChild(node);
        return;
    }

    activeRange.deleteContents();
    activeRange.insertNode(node);
    placeCaretAfter(document, node);
};

const ZERO_WIDTH_SPACE = '\u200B';

const isEffectivelyEmptyFormat = (element: Element): boolean => {
    const text = (element.textContent ?? '').split(ZERO_WIDTH_SPACE).join('');
    return text === '' && !element.querySelector('img, br, hr, video, svg');
};

/** Place caret in a ZWSP host so typing does not gravitate back into an adjacent format tag. */
const placeUnformattedCaret = (document: Document, parent: Node, before: Node | null): void => {
    const host = document.createTextNode(ZERO_WIDTH_SPACE);
    parent.insertBefore(host, before);
    const next = document.createRange();
    next.setStart(host, 1);
    next.collapse(true);
    restoreRange(document, next);
};

/** Enable/disable bold|italic|underline at a collapsed caret for subsequent typing. */
const toggleCollapsedInlineFormat = (document: Document, range: Range, format: 'bold' | 'italic' | 'underline'): void => {
    const tags = INLINE_TOGGLE_TAGS[format];
    const tagName = tags[0];
    const existing = closestFromNode(range.startContainer, tags.join(','));

    if (existing) {
        const parent = existing.parentNode;
        if (!parent) return;

        const afterRange = document.createRange();
        afterRange.selectNodeContents(existing);
        afterRange.setStart(range.startContainer, range.startOffset);
        const fragment = afterRange.extractContents();

        let afterEl: HTMLElement | null = null;
        const afterText = (fragment.textContent ?? '').split(ZERO_WIDTH_SPACE).join('');
        const afterHasMedia = Boolean(fragment.querySelector?.('img, br, hr, video, svg'));
        if (fragment.childNodes.length > 0 && (afterText !== '' || afterHasMedia)) {
            afterEl = document.createElement(tagName);
            afterEl.appendChild(fragment);
            parent.insertBefore(afterEl, existing.nextSibling);
        }

        if (isEffectivelyEmptyFormat(existing)) {
            const reference = afterEl ?? existing.nextSibling;
            existing.remove();
            placeUnformattedCaret(document, parent, reference);
        } else {
            placeUnformattedCaret(document, parent, afterEl ?? existing.nextSibling);
        }
        return;
    }

    // ZWSP keeps the wrapper alive when the toolbar steals focus briefly.
    const wrapper = document.createElement(tagName);
    const marker = document.createTextNode(ZERO_WIDTH_SPACE);
    wrapper.appendChild(marker);
    range.insertNode(wrapper);
    const inside = document.createRange();
    inside.setStart(marker, 1);
    inside.collapse(true);
    restoreRange(document, inside);
};

export const toggleInlineFormat = (document: Document, range: Range | null, format: 'bold' | 'italic' | 'underline'): void => {
    if (!range) return;

    if (range.collapsed) {
        toggleCollapsedInlineFormat(document, range, format);
        return;
    }

    const tags = INLINE_TOGGLE_TAGS[format];
    const existing = closestFromNode(range.commonAncestorContainer, tags.join(','));
    if (existing) {
        unwrapElement(existing);
        return;
    }

    wrapRangeWithElement(document, range, tags[0]);
};

const applySpanStyle = (document: Document, range: Range, apply: (_element: HTMLElement) => void): void => {
    const selectContents = (element: HTMLElement) => {
        range.selectNodeContents(element);
        restoreRange(document, range);
    };

    const existing = closestFromNode(range.commonAncestorContainer, 'span');
    const existingElement = existing?.tagName === 'SPAN' ? (existing as HTMLElement) : null;
    if (existingElement && existingElement.childNodes.length === 1 && range.toString() === existingElement.textContent) {
        apply(existingElement);
        selectContents(existingElement);
        return;
    }

    const wrapper = wrapRangeWithElement(document, range, 'span');
    if (!wrapper) return;
    apply(wrapper);
    selectContents(wrapper);
};

/** Remove stale font-size on outer spans so toolbar reads match the text the user sees. */
const clearAncestorFontSizes = (element: HTMLElement): void => {
    let ancestor = element.parentElement;
    while (ancestor) {
        if (ancestor.tagName === 'SPAN' && ancestor.style.fontSize) {
            ancestor.style.removeProperty('font-size');
        }
        ancestor = ancestor.parentElement;
    }
};

/** Drop empty single-child wrapper spans left after clearing nested font-size. */
const unwrapEmptyAncestorSpans = (element: HTMLElement): void => {
    let current = element.parentElement;
    while (current?.tagName === 'SPAN') {
        const parent = current.parentElement;
        const style = current.getAttribute('style')?.trim() ?? '';
        if (!style && !current.className) {
            const host = current.parentNode;
            while (current.firstChild) {
                host?.insertBefore(current.firstChild, current);
            }
            current.remove();
        }
        current = parent;
    }
};

/** Collapse nested single-child spans so font-size edits stay on one element. */
const flattenNestedSpans = (span: HTMLElement): HTMLElement => {
    let current = span;
    while (current.parentElement?.tagName === 'SPAN' && current.parentElement.childNodes.length === 1) {
        const outer = current.parentElement;
        const parent = outer.parentNode;
        parent?.insertBefore(current, outer);
        outer.remove();
    }
    while (current.childElementCount === 1 && current.firstElementChild?.tagName === 'SPAN' && current.childNodes.length === 1) {
        const inner = current.firstElementChild as HTMLElement;
        const parent = current.parentNode;
        if (!parent) break;
        parent.insertBefore(inner, current);
        current.remove();
        current = inner;
    }
    return current;
};

export const applyForeColor = (document: Document, range: Range | null, color: string): void => {
    if (!range || range.collapsed) return;
    applySpanStyle(document, range, (element) => {
        element.style.color = color;
    });
};

export const applyFontSize = (document: Document, range: Range | null, size: string): void => {
    if (!range || range.collapsed) return;

    const selectContents = (element: HTMLElement) => {
        range.selectNodeContents(element);
        restoreRange(document, range);
    };

    let span = closestFromNode(range.commonAncestorContainer, 'span') as HTMLElement | null;
    if (span?.tagName !== 'SPAN') {
        applySpanStyle(document, range, (element) => {
            element.style.fontSize = size;
            clearAncestorFontSizes(element);
        });
        return;
    }

    span = flattenNestedSpans(span);

    // If we are already inside a sized run, update it — never nest another font-size span.
    if (span.style.fontSize || range.toString() === (span.textContent ?? '')) {
        span.style.fontSize = size;
        clearAncestorFontSizes(span);
        unwrapEmptyAncestorSpans(span);
        selectContents(span);
        return;
    }

    applySpanStyle(document, range, (element) => {
        element.style.fontSize = size;
        clearAncestorFontSizes(element);
        unwrapEmptyAncestorSpans(element);
    });
};

export const applyFontFamily = (document: Document, range: Range | null, family: string): void => {
    if (!range || range.collapsed) return;
    applySpanStyle(document, range, (element) => {
        element.style.fontFamily = family;
    });
};

export const applyHighlight = (document: Document, range: Range | null, color: string): void => {
    if (!range || range.collapsed) return;
    applySpanStyle(document, range, (element) => {
        element.style.backgroundColor = color;
    });
};

export const applyFormatBlock = (document: Document, range: Range | null, block: 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'): void => {
    const node = range?.startContainer ?? null;
    const current = closestFromNode(node, BLOCK_SELECTOR) as HTMLElement | null;
    if (!current) return;

    const tag = current.tagName.toLowerCase();
    if (tag === 'td' || tag === 'th' || tag === 'li') {
        const wrapper = document.createElement(block);
        while (current.firstChild) wrapper.appendChild(current.firstChild);
        current.appendChild(wrapper);
        return;
    }

    if (tag === block) return;

    const replacement = document.createElement(block);
    Array.from(current.attributes).forEach((attribute) => replacement.setAttribute(attribute.name, attribute.value));
    while (current.firstChild) replacement.appendChild(current.firstChild);
    current.replaceWith(replacement);
};

export const applyList = (document: Document, range: Range | null, listTag: 'ul' | 'ol'): void => {
    const node = range?.startContainer ?? null;
    const listItem = closestFromNode(node, 'li');
    const existingList = listItem?.parentElement;

    if (listItem && existingList && ['ul', 'ol'].includes(existingList.tagName.toLowerCase())) {
        if (existingList.tagName.toLowerCase() === listTag) {
            const paragraph = document.createElement('p');
            while (listItem.firstChild) paragraph.appendChild(listItem.firstChild);
            existingList.replaceWith(paragraph);
            return;
        }

        const replacement = document.createElement(listTag);
        while (existingList.firstChild) replacement.appendChild(existingList.firstChild);
        existingList.replaceWith(replacement);
        return;
    }

    const block = closestFromNode(node, 'p, h1, h2, h3, h4, h5, h6, div') as HTMLElement | null;
    const list = document.createElement(listTag);
    const item = document.createElement('li');

    if (block) {
        while (block.firstChild) item.appendChild(block.firstChild);
        if (!item.firstChild) item.appendChild(document.createTextNode('\u00a0'));
        list.appendChild(item);
        block.replaceWith(list);
        return;
    }

    item.appendChild(document.createTextNode(range?.toString() || '\u00a0'));
    list.appendChild(item);
    insertNodeAtRange(document, range, list);
};

export const insertHorizontalRule = (document: Document, range: Range | null): void => {
    insertNodeAtRange(document, range, document.createElement('hr'));
};

export const insertImage = (document: Document, range: Range | null, src: string, alt: string): void => {
    const image = document.createElement('img');
    image.setAttribute('src', src);
    image.setAttribute('alt', alt);
    image.setAttribute('style', 'max-width: 100%; height: auto;');
    insertNodeAtRange(document, range, image);
};

export const applyLink = (document: Document, range: Range | null, link: { href: string; text: string; target: string | null }): void => {
    const existing = getNearestAnchor(range?.startContainer ?? null);
    if (existing) {
        existing.setAttribute('href', link.href);
        existing.textContent = link.text;
        if (link.target) {
            existing.setAttribute('target', link.target);
            existing.setAttribute('rel', 'noopener noreferrer nofollow');
        } else {
            existing.removeAttribute('target');
            existing.removeAttribute('rel');
        }
        return;
    }

    const anchor = document.createElement('a');
    anchor.setAttribute('href', link.href);
    anchor.textContent = link.text;
    if (link.target) {
        anchor.setAttribute('target', link.target);
        anchor.setAttribute('rel', 'noopener noreferrer nofollow');
    }
    insertNodeAtRange(document, range, anchor);
};

export const unlinkAnchor = (_document: Document, range: Range | null): void => {
    const anchor = getNearestAnchor(range?.startContainer ?? elementFromSelectionNode(range?.commonAncestorContainer ?? null));
    if (!anchor) return;
    unwrapElement(anchor);
};
